import { StockStatusEnum } from '@petsmart-ui/sparky';

import type { CartUI } from '@libs/common-node';
import type { StoreV2 } from '@libs/common-types';
import { DeliveryType } from '@libs/core';

import { ItemsListItem, StockErrorItem } from '../types';
import { getStockLevelAvailability } from './stockAvailability';
import { getAllStockStatuses } from './stockStatus';

export type StockAvailabilityFlags = {
    autoshipEnabled: boolean | undefined;
    bopisEnabled: boolean | undefined;
    samedayDeliveryEnabled: boolean | undefined;
    shipToHomeEnabled: boolean | undefined;
};

interface EnrichItemsWithStockAvailabilityPayload {
    itemsList: ItemsListItem[];
    cart: CartUI;
    storeAvailability: Partial<StockAvailabilityFlags>;
    stockErrors?: StockErrorItem[];
    selectedStore: StoreV2 | null | undefined;
}

type EnrichedStockFields = {
    autoshipStockAvailability: string;
    sthStockAvailability: string;
    bopisStockAvailability: string;
    sddStockAvailability: string;
    sth_availability: 'yes' | 'no';
    bopis_availability: 'yes' | 'no';
    sdd_availability: 'yes' | 'no';
    storeId: string;
    fulfillmentMethod: DeliveryType;
};

export const enrichItemsWithStockAvailability = <T extends ItemsListItem>(
    payload: Omit<EnrichItemsWithStockAvailabilityPayload, 'itemsList'> & { itemsList: T[] }
): Array<T & EnrichedStockFields> | Array<T> => {
    const { itemsList, storeAvailability, cart, stockErrors = [], selectedStore } = payload;

    const getFulfillmentGroup = (itemId: string) =>
        cart?.fulfillmentGroups.find(group => group.items.some(item => item.id === itemId));

    const { autoshipEnabled, bopisEnabled, samedayDeliveryEnabled, shipToHomeEnabled } = storeAvailability;

    const globalStoreAvailability = {
        [DeliveryType.AUTOSHIP]: autoshipEnabled,
        [DeliveryType.BOPIS]: bopisEnabled,
        [DeliveryType.SDD]: samedayDeliveryEnabled,
        [DeliveryType.STH]: shipToHomeEnabled,
        [DeliveryType.STM]: false,
    };

    return itemsList.map(item => {
        const fulfillmentGroup = getFulfillmentGroup(item.id);
        if (!fulfillmentGroup) return item;

        const stockStatuses = getAllStockStatuses({
            item,
            fulfillmentGroup,
            selectedStore,
            globalStoreAvailability,
            stockErrors,
        });

        const sthEnum =
            stockStatuses[DeliveryType.AUTOSHIP] === StockStatusEnum.OutOfStock ||
            stockStatuses[DeliveryType.STH] === StockStatusEnum.OutOfStock
                ? StockStatusEnum.OutOfStock
                : stockStatuses[DeliveryType.STH];

        return {
            ...item,
            autoshipStockAvailability: getStockLevelAvailability(stockStatuses[DeliveryType.AUTOSHIP]) ?? '',
            sthStockAvailability: getStockLevelAvailability(sthEnum) ?? '',
            bopisStockAvailability: getStockLevelAvailability(stockStatuses[DeliveryType.BOPIS]) ?? '',
            sddStockAvailability: getStockLevelAvailability(stockStatuses[DeliveryType.SDD]) ?? '',
            sth_availability: shipToHomeEnabled ? 'yes' : 'no',
            bopis_availability: bopisEnabled ? 'yes' : 'no',
            sdd_availability: samedayDeliveryEnabled ? 'yes' : 'no',
            storeId: String(fulfillmentGroup.storeId),
            fulfillmentMethod: fulfillmentGroup.fulfillmentMethod,
        };
    });
};
